import React from 'react';
import { Typography, TextField, Box } from '@mui/material';

// Note: Validation functions are now handled by the parent hook

import type { CertificationInfo } from '../../types/company.types';

import { getStylesForEditableField } from '@/utils/helpers';
import { PaperBoxStyles } from '@/components/styles';

interface CertificationsSectionProps {
  certificationInfo: CertificationInfo;
  originalCertificationInfo?: CertificationInfo;
  isEditMode?: boolean;
  onUpdateField: (field: string, value: string) => void;
  getFieldError: (field: string) => string | undefined;
  isFieldValid: (field: string) => boolean;
}

export const CertificationsSection: React.FC<CertificationsSectionProps> = ({
  certificationInfo,
  originalCertificationInfo,
  isEditMode,
  onUpdateField,
  getFieldError,
  isFieldValid,
}) => {
  // Note: Validation is now handled by the parent hook to ensure proper touched field checking

  return (
    <PaperBoxStyles elevation={1} sx={{ mb: 2, paddingBottom: '32px' }}>
      <Typography
        variant='h5'
        fontWeight='bold'
        sx={{
          mb: 3,
          color: '#1A1C20',
          fontSize: '22px',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        認証情報
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* First Row - JIS Number and Certification Type */}
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          {/* 日本工業規格認証番号 */}
          <Box sx={{ flex: 1, minWidth: '300px' }}>
            <TextField
              label='日本工業規格認証番号'
              placeholder='日本工業規格認証番号を入力'
              value={certificationInfo.jisNumber || ''}
              onChange={(e) => {
                const value = e.target.value;
                onUpdateField('jisNumber', value);
              }}
              error={!isFieldValid('certificationInfo.jisNumber')}
              helperText={getFieldError('certificationInfo.jisNumber')}
              fullWidth
              slotProps={{
                input: {
                  inputProps: { maxLength: 50 },
                  sx: getStylesForEditableField(
                    !!isEditMode,
                    originalCertificationInfo?.jisNumber,
                    certificationInfo.jisNumber
                  ),
                },
              }}
            />
          </Box>

          {/* 認証の区分 */}
          <Box sx={{ flex: 1, minWidth: '300px' }}>
            <TextField
              label='認証の区分'
              placeholder='認証の区分を入力'
              value={certificationInfo.certificationType || ''}
              onChange={(e) => {
                const value = e.target.value;
                onUpdateField('certificationType', value);
              }}
              error={!isFieldValid('certificationInfo.certificationType')}
              helperText={getFieldError('certificationInfo.certificationType')}
              fullWidth
              slotProps={{
                input: {
                  inputProps: { maxLength: 50 },
                  sx: getStylesForEditableField(
                    !!isEditMode,
                    originalCertificationInfo?.certificationType,
                    certificationInfo.certificationType
                  ),
                },
              }}
            />
          </Box>
        </Box>

        {/* Second Row - High Strength Number (Full Width) */}
        <Box>
          <TextField
            label='高強度大臣認定番号'
            placeholder='高強度大臣認定番号を入力'
            value={certificationInfo.highStrengthNumber || ''}
            onChange={(e) => {
              const value = e.target.value;
              onUpdateField('highStrengthNumber', value);
            }}
            error={!isFieldValid('certificationInfo.highStrengthNumber')}
            helperText={getFieldError('certificationInfo.highStrengthNumber')}
            fullWidth
            slotProps={{
              input: {
                inputProps: { maxLength: 50 },
                sx: getStylesForEditableField(
                  !!isEditMode,
                  originalCertificationInfo?.highStrengthNumber,
                  certificationInfo.highStrengthNumber
                ),
              },
            }}
          />
        </Box>
      </Box>
    </PaperBoxStyles>
  );
};
